/* Tiny Install-to-Home-Screen prompt.
 * - Captures Chrome/Edge `beforeinstallprompt` (deferred install)
 * - Detects iOS Safari (no native install API) and shows manual instructions
 * - Dismissible; remembers dismissal in localStorage for 14 days
 * - Does not appear in already-installed PWAs (display-mode: standalone) */
import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const DISMISS_KEY = "clinq-pwa-install-dismissed";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true);

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (ts && Date.now() - Number(ts) < DISMISS_DAYS * 86400e3) return;
    } catch (e) {
      // localStorage can throw in private browsing — fall through and show the prompt.
      if (process.env.NODE_ENV !== "production") console.debug("[install] dismiss-flag read failed:", e?.message);
    }

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari — show our manual instructions after a short delay
    if (isIOS()) {
      const t = setTimeout(() => { setIosTip(true); setShow(true); }, 3500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("[install] dismiss-flag write failed:", e?.message);
    }
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      // userChoice can reject when the browser tab loses focus; safe to ignore.
      if (process.env.NODE_ENV !== "production") console.debug("[install] userChoice rejected:", e?.message);
    }
    setShow(false);
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      role="dialog"
      aria-labelledby="install-prompt-title"
      className="fixed inset-x-3 bottom-3 z-40 mx-auto max-w-md rounded-2xl border border-[#0A8B85]/25 bg-white p-4 shadow-[0_18px_50px_-10px_rgba(10,139,133,0.4)] sm:inset-x-auto sm:right-4 sm:bottom-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0A8B85] text-white">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p id="install-prompt-title" className="text-sm font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Install Clinq
          </p>
          {iosTip ? (
            <p className="mt-1 text-xs leading-relaxed text-neutral-600">
              Tap the <Share className="-mt-0.5 inline h-3.5 w-3.5 text-[#0A8B85]" /> share icon in Safari, then{" "}
              <b>Add to Home Screen</b>. Opens like a real app.
            </p>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-neutral-600">
              Add Clinq to your home screen for a faster, full-screen experience — no browser bar.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="install-prompt-dismiss"
          className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {!iosTip && deferred && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            onClick={dismiss}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-100"
          >
            Not now
          </button>
          <button
            onClick={install}
            data-testid="install-prompt-install"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#066667]"
          >
            <Download className="h-3.5 w-3.5" /> Install
          </button>
        </div>
      )}
    </div>
  );
}
